import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Button,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import LocationSearch from "../Component/LocationSearch";
import { toast } from "react-toastify";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import Constants from "../../config/Constants";
import { reverseGeocodeToSuburb } from "../../Utils/func";
import {
  PageHeader,
  Card,
  DataTable,
  StatusBadge,
  SearchInput,
  ActionButton,
  UserDetailsModal,
  ConfirmDialog,
} from "../shared";
import { useTheme } from "@mui/material/styles";

// Simple in-memory cache to avoid repeated reverse-geocoding calls
const adminLocationCache = {};

const User = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const PAGE_LIMIT = 10;
  
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const token = props?.props?.profile?.token;

  const [datalist, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userStatus, setUserStatus] = useState({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [userDetailsModal, setUserDetailsModal] = useState({
    open: false,
    userId: null,
    role: null,
    locationLabel: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: 'warning', title: '', message: '', action: null });
  const [isExporting, setIsExporting] = useState(false);
  const [sportsList, setSportsList] = useState([]);
  const [orgsDialog, setOrgsDialog] = useState({ open: false, user: null, orgs: [] });
  
  // Comprehensive filters
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    sport_id: "",
    search: "",
    location: "",
    user_latitude: "",
    user_longitude: "",
    radius: "",
  });

  // Load sports list
  useEffect(() => {
    if (token && (userType === "admin" || userType === "master_org")) {
      loadSports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userType]);

  // Initialize user statuses from data list
  useEffect(() => {
    const initialStatuses = {};
    datalist.forEach((user) => {
      initialStatuses[user.id] = user.status === "active";
    });
    setUserStatus(initialStatuses);
  }, [datalist]);

  const loadSports = async () => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.SPORT_LIST_COMMON,
        {},
        headers
      );
      if (data?.status && Array.isArray(data.data)) {
        setSportsList(data.data);
      }
    } catch (error) {
      console.error("Failed to load sports:", error);
    }
  };

  // Fetch users (only users, not orgs) - uses filters state directly
  const fetchUsers = async (pageNum = 1) => {
    if (!token) return;
    setLoading(true);

    // Always use current filters state
    const search = filters.search || "";
    const role = filters.role || "";
    const status = filters.status || "";
    const sport_id = filters.sport_id || "";


    if (userType === "master_org") {
      // sport_id is required by backend - use filter or first sport from profile or default to 1
      const sportId = sport_id || 
                     props?.props?.profile?.sportList?.[0]?.id || 
                     props?.props?.profile?.sportIdList?.[0] || 
                     1;
      const params = new URLSearchParams();
      params.append("sport_id", String(sportId));
      params.append("limit", String(PAGE_LIMIT));
      if (search) {
        params.append("search", search);
      }
      if (role) {
        params.append("role", role);
      }
      if (status) {
        params.append("status", status);
      }
      // Add location filter to query params (backend handles location filtering)
      if (filters.user_latitude && filters.user_longitude) {
        params.append("user_latitude", filters.user_latitude);
        params.append("user_longitude", filters.user_longitude);
        // Use default 1000 if radius is empty
        params.append("radius", filters.radius || "1000");
      }
      const url = `${ApiEndpoint.MASTER_ORG_USERS}?${params.toString()}`;
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      const data = await ApiServices.GetApiCall(url, headers);
      setLoading(false);
      if (data?.status) {
        let allData = Array.isArray(data.data?.users)
          ? data.data.users
          : Array.isArray(data.data)
          ? data.data
          : [];
        const usersOnly = allData.filter(
          (item) => !["schools", "individual", "master_org", "sub_org", "master"].includes(item.role)
        );

        // Backend handles location filtering - no client-side filtering needed
        setDataList(usersOnly);
        setTotalUsers(data.count || usersOnly.length);
      } else {
        setDataList([]);
        setTotalUsers(0);
      }
      return;
    }

    const body = {
      type: "user",
      pagination: true,
      page: pageNum,
      limit: PAGE_LIMIT,
      srch: search || "",
    };
    // Filter by role if specified
    if (role && !["schools", "individual", "master_org", "sub_org", "master"].includes(role)) {
      body.role = role;
    }
    // Filter by status if specified
    if (status) {
      body.status = status;
    }
    // Filter by sport if specified - convert to integer for backend
    if (sport_id) {
      const sportIdNum = parseInt(sport_id, 10);
      if (!isNaN(sportIdNum) && sportIdNum > 0) {
        body.sport_id = sportIdNum;
      }
    }
    // Add location filter to request body (backend handles location filtering)
    if (filters.user_latitude && filters.user_longitude) {
      body.user_latitude = parseFloat(filters.user_latitude);
      body.user_longitude = parseFloat(filters.user_longitude);
      // Use default 1000 if radius is empty
      body.radius = parseFloat(filters.radius || "1000");
    }
    // Backend behavior: type: 'user' without role excludes organizations automatically

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_USER_LIST,
        JSON.stringify(body),
        headers
      );
      
      setLoading(false);
      if (data?.status) {
        // Backend now filters out organizations when type: 'user' is sent (unless role: 'schools' is explicitly provided)
        // Keep client-side filtering as a safety net
        let allData = Array.isArray(data.data) ? data.data : [];
        const usersOnly = allData.filter(
          (item) => !["schools", "individual", "master_org", "sub_org", "master"].includes(item.role)
        );

        // Backend handles location filtering - no client-side filtering needed
        setDataList(usersOnly);
        setTotalUsers(usersOnly.length === allData.length ? (data.count || usersOnly.length) : usersOnly.length);
      } else {
        // Backend returned status: false - check for error message
        setDataList([]);
        setTotalUsers(0);
        if (data?.message) {
          toast.error(`Failed to fetch users: ${data.message}`);
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    }
  };

  // Fetch users when filters or page changes
  useEffect(() => {
    if (token) {
      fetchUsers(page + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, filters.role, filters.status, filters.sport_id, filters.search, filters.user_latitude, filters.user_longitude, userType]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFilters(prev => ({ ...prev, search: value }));
    setPage(0);
  };

  // Delete user (for admin and master org)
  const handleDelete = async (id) => {
    if (userType !== "admin" && userType !== "master_org") return;
    setConfirmDialog({
      open: true,
      type: 'error',
      title: 'Delete User?',
      message: 'This action cannot be undone. The user will be permanently removed.',
      action: async () => {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
        };
        props.setIsLoaded(true);
        const data = await ApiServices.PostApiCall(
          ApiEndpoint.UMPHIRE_DELETE,
          JSON.stringify({ id_user: id }),
          headers
        );
        props.setIsLoaded(false);
        if (data?.status) {
          toast.success("User deleted");
          fetchUsers(page + 1);
        }
      },
    });
  };

  // Toggle user status (for admin and master org)
  const handleToggleStatus = async (userId) => {
    if (userType !== "admin" && userType !== "master_org") return;
    const currentStatus = userStatus[userId] ? "active" : "inactive";
    setConfirmDialog({
      open: true,
      type: 'warning',
      title: 'Change Status?',
      message: `Are you sure you want to change this user's status from ${currentStatus} to ${currentStatus === 'active' ? 'inactive' : 'active'}?`,
      action: async () => {
        const newStatus = userStatus[userId] ? "inactive" : "active";
        const headers = {
          "Content-Type": "application/json",
          "X-Access-Token": token,
        };
        const response = await ApiServices.PostApiCall(
          ApiEndpoint.ACTIVE_DEACTIVE_USER,
          JSON.stringify({ id: userId, status: newStatus }),
          headers
        );
        if (response?.data) {
          setUserStatus((prev) => ({
            ...prev,
            [userId]: response.data.status === "active",
          }));
          toast.success("Status updated");
          fetchUsers(page + 1);
        }
      },
    });
  };

  // Format role display
  const formatRole = (role) => {
    const roleMap = {
      referee: "Referee",
      coach: "Coach",
      player: "Player",
      parent: "Parent",
    };
    return roleMap[role] || role;
  };

  // Get role for API call
  const getRoleForApi = (role) => {
    if (["coach", "referee", "player", "parent"].includes(role)) {
      return role;
    }
    return role;
  };

  const LocationCell = ({ row }) => {
    const [label, setLabel] = useState("");

    useEffect(() => {
      // Prefer backend-provided suburb/city (user_suburb set on save from backend geocode)
      const city =
        row.user_suburb ||
        row.city ||
        row.town ||
        row.suburb ||
        row.locality ||
        row.user_city ||
        "";

      if (city) {
        setLabel(city);
        return;
      }

      // If we have an explicit user_address/location string but no city,
      // fall back to showing that once (older data)
      const addressLine =
        row.user_address ||
        row.location ||
        row.address ||
        row.address_line ||
        row.full_address ||
        "";

      if (addressLine) {
        setLabel(addressLine);
        return;
      }

      const lat = parseFloat(row.user_latitude || row.latitude);
      const lng = parseFloat(row.user_longitude || row.longitude);

      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        setLabel("-");
        return;
      }

      const cacheKey = `${lat},${lng}`;
      if (adminLocationCache[cacheKey]) {
        setLabel(adminLocationCache[cacheKey]);
        return;
      }

      let isMounted = true;
      const apiKey = Constants.MAP_API_KEY;

      const fetchLocation = async () => {
        try {
          const display = await reverseGeocodeToSuburb(lat, lng, apiKey);
          adminLocationCache[cacheKey] = display;
          if (isMounted) setLabel(display);
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
          if (isMounted) setLabel(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
      };

      fetchLocation();

      return () => {
        isMounted = false;
      };
    }, [row]);

    const handleClick = () => {
      if (!label) return;
      const roleForApi = getRoleForApi(row.role);
      setUserDetailsModal({
        open: true,
        userId: row.id,
        role: roleForApi,
        locationLabel: label,
      });
    };

    return (
      <Typography
        onClick={handleClick}
        title={label || ""}
        sx={{
          color: isDark ? "#cbd5e1" : "#4a5568",
          fontSize: "0.75rem",
          cursor: label ? "pointer" : "default",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 260,
        }}
      >
        {label || "-"}
      </Typography>
    );
  };

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "User",
      width: 400,
      render: (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={row.profile_image}
            sx={{
              width: 40,
              height: 40,
              bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {row.first_name?.[0]}
            {row.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {row.first_name} {row.last_name}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "sports",
      label: "Sports",
      width: 160,
      mobileSameLineGroup: "info",
      render: (_, row) => {
        // Backend exposes a unified sports_list per user:
        // [{ id, name, source: 'primary' | 'organisation' }, ...]
        let sports = [];

        if (Array.isArray(row.sports_list)) {
          sports = row.sports_list
            .map((s) => s?.name || s?.sport_name || s?.title || "")
            .filter(Boolean);
        }

        // Legacy/alternate shapes as fallbacks
        if (!sports.length && Array.isArray(row.sports)) {
          sports = row.sports
            .map((s) => s?.name || s?.sport_name || s?.title || "")
            .filter(Boolean);
        }

        const singleSport =
          row.sport_name ||
          row.sport ||
          row.primary_sport ||
          row.user_sport ||
          "";

        if (!sports.length && singleSport) {
          sports = [singleSport];
        }

        const uniqueSports = [...new Set(sports)];
        if (!uniqueSports.length) {
          return (
            <Typography
              sx={{ color: isDark ? "#cbd5e1" : "#4a5568", fontSize: "0.875rem" }}
            >
              -
            </Typography>
          );
        }

        const visibleSports = uniqueSports.slice(0, 2);
        const remainingCount = uniqueSports.length - visibleSports.length;
        const label = remainingCount > 0
          ? `${visibleSports.join(", ")} +${remainingCount}`
          : visibleSports.join(", ");

        const handleClick = (e) => {
          e.stopPropagation();
          const roleForApi = getRoleForApi(row.role);
          setUserDetailsModal({
            open: true,
            userId: row.id,
            role: roleForApi,
            locationLabel: userDetailsModal.locationLabel || "",
          });
        };

        return (
          <Typography
            onClick={handleClick}
            title={uniqueSports.join(", ")}
            sx={{
              color: isDark ? "#cbd5e1" : "#4a5568",
              fontSize: "0.75rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography>
        );
      },
    },
    {
      field: "location",
      label: "Location",
      width: 260,
      align: "right",
      mobileSameLineGroup: "info",
      render: (_, row) => <LocationCell row={row} />,
    },
    // Organizations column - only for admin
    ...(userType === "admin" ? [{
      field: "organizations",
      label: "Clubs",
      width: 200,
      mobileInlineWithTitle: true,
      render: (_, row) => {
        // Check various possible field names for organization data
        // Backend should return org info as an ARRAY since users can belong to multiple orgs
        let orgs = row.organisations || 
                   row.organizations || 
                   row.organisation || 
                   row.organization ||
                   row.sub_orgs || 
                   row.sub_org || 
                   row.subOrgs || 
                   row.subOrg || 
                   row.orgs ||
                   row.org_details ||
                   [];
        
        // Handle case where it might be a single object (backward compatibility)
        if (!Array.isArray(orgs)) {
          // If it's a single object, convert to array
          if (orgs && typeof orgs === 'object' && Object.keys(orgs).length > 0) {
            orgs = [orgs];
          } else {
            orgs = [];
          }
        }
        
        // Also check if org data is nested in user or other objects
        const orgsFromUser = row.user?.organisations || 
                           row.user?.organizations ||
                           row.user?.organisations_details ||
                           row.user?.organizations_details ||
                           [];
        
        if (Array.isArray(orgsFromUser) && orgsFromUser.length > 0) {
          orgs = [...orgs, ...orgsFromUser];
        } else if (orgsFromUser && typeof orgsFromUser === 'object') {
          orgs = [...orgs, orgsFromUser];
        }
        
        // Remove duplicates based on org ID
        const uniqueOrgs = [];
        const seenIds = new Set();
        
        orgs.forEach(org => {
          const orgId = org.id || org.organisation_id || org.organization_id;
          const orgName = org.name || 
                        org.organisation_name || 
                        org.organization_name ||
                        org.org_name ||
                        "";
          
          // Only include if it has a name and we haven't seen this ID before
          if (orgName) {
            if (orgId && !seenIds.has(orgId)) {
              seenIds.add(orgId);
              uniqueOrgs.push(org);
            } else if (!orgId) {
              // If no ID, check by name to avoid duplicates
              const nameExists = uniqueOrgs.some(o => 
                (o.name || o.organisation_name || o.organization_name || o.org_name) === orgName
              );
              if (!nameExists) {
                uniqueOrgs.push(org);
              }
            }
          }
        });
        
        // If no org data, show "-"
        if (uniqueOrgs.length === 0) {
          return (
            <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>
              -
            </Typography>
          );
        }
        
        // Display logos in a row with "+X" indicator (always show max 2 avatars, then "+X")
        const maxVisible = 2;
        const visibleOrgs = uniqueOrgs.slice(0, maxVisible);
        const remainingCount = uniqueOrgs.length - maxVisible;
        
        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOrgsDialog({ open: true, user: row, orgs: uniqueOrgs });
            }}
          >
            {visibleOrgs.map((org, index) => {
              const logoUrl = org.logo_url || org.logo || "";
              const orgName = org.name || 
                             org.organisation_name || 
                             org.organization_name ||
                             org.org_name ||
                             "";
              
              if (!orgName) return null;
              
              return (
                <Avatar
                  key={org.id || index}
                  src={logoUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    color: isDark ? "#818cf8" : "#1976d2",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                    marginLeft: index > 0 ? "-8px" : 0,
                    zIndex: maxVisible - index,
                    "&:hover": {
                      zIndex: 10,
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  {orgName[0]?.toUpperCase() || "O"}
                </Avatar>
              );
            })}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                  marginLeft: "-8px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.3)" : "rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOrgsDialog({ open: true, user: row, orgs: uniqueOrgs });
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </Box>
        );
      },
    }] : []),
    {
      field: "role",
      label: "Role",
      width: 340,
      minWidth: 90,
      mobileSameLineGroup: "meta",
      render: (value) => (
        <Chip
          label={formatRole(value)}
          size="small"
          sx={{
            backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
            color: isDark ? "#818cf8" : "#1976d2",
            fontWeight: 500,
            fontSize: "0.75rem",
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      field: "status",
      label: "Status",
      width: 20,
      minWidth: 100,
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value) => <StatusBadge status={value} />,
    },
  ];

  // Actions column - right aligned
  const renderActions = (row) => (
    <Box 
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
      {(userType === "admin" || userType === "master_org") && (
        <>
          {["referee", "player", "coach", "parent"].includes(
            row.role
          ) && (
            <Tooltip title="Delete user">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row.id);
                }}
                sx={{
                  color: isDark ? "#cbd5e1" : "#718096",
                  "&:hover": { backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2", color: "#ef4444" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {(userType === "admin" || userType === "master_org") && (
            <Tooltip 
              title={userStatus[row.id] 
                ? `Change user status from Active to Inactive` 
                : `Change user status from Inactive to Active`}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={userStatus[row.id] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleStatus(row.id);
                    }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: isDark ? "#6366f1" : "#1976d2",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: isDark ? "#6366f1" : "#1976d2",
                      },
                    }}
                  />
                }
                label=""
                sx={{ ml: 0, mr: 0 }}
              />
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleLocationSelect = (e, locationData) => {
    if (locationData && locationData.lat && locationData.lng) {
      // Location was selected from Google Autocomplete
      setFilters(prev => ({
        ...prev,
        location: locationData.address || prev.location,
        user_latitude: locationData.lat?.toString() || "",
        user_longitude: locationData.lng?.toString() || "",
      }));
      setPage(0);
    } else if (e && e.target) {
      // Text input changed (manual typing)
      setFilters(prev => ({
        ...prev,
        location: e.target.value,
        // Clear lat/lng if location is cleared
        user_latitude: e.target.value ? prev.user_latitude : "",
        user_longitude: e.target.value ? prev.user_longitude : "",
      }));
    }
  };

  const clearFilters = () => {
    setFilters({
      role: "",
      status: "",
      sport_id: "",
      search: "",
      location: "",
      user_latitude: "",
      user_longitude: "",
      radius: "",
    });
    setSearchQuery("");
    setPage(0);
  };

  const hasActiveFilters = filters.role || filters.status || filters.sport_id || filters.search || filters.location;

  // Export users to CSV
  const handleExport = async () => {
    if (!token) return;
    
    // Warn if current list is empty and filters are applied
    if (hasActiveFilters && totalUsers === 0) {
      const confirm = window.confirm(
        `No users found matching the current filters. The export will be empty.\n\n` +
        `Active filters:\n` +
        `${filters.role ? `- Role: ${filters.role}\n` : ''}` +
        `${filters.status ? `- Status: ${filters.status}\n` : ''}` +
        `${filters.sport_id ? `- Sport: ${sportsList.find(s => s.id.toString() === filters.sport_id)?.name || filters.sport_id}\n` : ''}` +
        `${filters.search ? `- Search: ${filters.search}\n` : ''}` +
        `${filters.location ? `- Location: ${filters.location}\n` : ''}` +
        `\nDo you want to continue with the export?`
      );
      if (!confirm) {
        return;
      }
    }
    
    setIsExporting(true);
    
    try {
      // Build query string from current filters (same as list view)
      const queryParams = new URLSearchParams();
      if (filters.role) {
        queryParams.append('role', filters.role);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.sport_id) {
        queryParams.append('sport_id', filters.sport_id);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      // Location filters (require both lat and lng)
      if (filters.user_latitude && filters.user_longitude) {
        queryParams.append('user_latitude', filters.user_latitude);
        queryParams.append('user_longitude', filters.user_longitude);
        // Use default 1000 if radius is empty
        queryParams.append('radius', filters.radius || '1000');
      }
      // Add type=user to exclude schools (admin only)
      if (userType === "admin") {
        queryParams.append('type', 'user');
      }

      const endpoint = userType === "admin" 
        ? ApiEndpoint.ADMIN_USER_EXPORT
        : ApiEndpoint.MASTER_ORG_USER_EXPORT;

      const queryString = queryParams.toString();
      const url = `${endpoint}${queryString ? '?' + queryString : ''}`;
      
      const headers = {
        "x-access-token": token,
      };

      const { blob, filename } = await ApiServices.GetBlobApiCall(url, headers);
      
      if (!blob) {
        throw new Error('Export failed - no data returned');
      }

      // Modify filename to include location text if location filter is applied
      let finalFilename = filename;
      if (filters.user_latitude && filters.user_longitude && filters.location) {
        // Sanitize location text for filename (remove special chars, replace spaces with underscores)
        const sanitizedLocation = filters.location
          .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .substring(0, 50) // Limit length
          .trim();
        
        if (sanitizedLocation) {
          // Insert location before .csv extension
          const nameWithoutExt = finalFilename.replace(/\.csv$/i, '');
          finalFilename = `${nameWithoutExt}_${sanitizedLocation}.csv`;
        }
      }

      // Check if blob is empty (CSV with only headers)
      const blobSize = blob.size;
      
      if (blobSize < 100) {
        // Very small file likely means only headers or empty
        const text = await blob.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length <= 1) {
          toast.warning('Export completed but no data found matching the filters. The CSV may only contain headers.');
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Export successful! File: ${finalFilename} (${(blobSize / 1024).toFixed(2)} KB)`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Export failed. Check console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  // Role filter options (excluding orgs)
  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "referee", label: "Referee" },
    { value: "coach", label: "Coach" },
    { value: "player", label: "Player" },
    { value: "parent", label: "Parent" },
  ];

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={`Manage all ${userType === "admin" ? "platform" : "club"} users`}
        breadcrumbs={[{ label: "Admin" }, { label: "Users" }]}
      >
        {/* Search and Filter Controls */}
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={() => {
            setSearchQuery("");
            handleFilterChange('search', '');
          }}
          placeholder="Search by name or email..."
          width={280}
        />

        <ActionButton
          variant="secondary"
          startIcon={<FilterListIcon />}
          onClick={(e) => {
            e.currentTarget.blur(); // Remove focus from button before opening dialog
            setFilterDialogOpen(true);
          }}
        >
          Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v && v !== "1000").length})`}
        </ActionButton>

        <ActionButton
          variant="secondary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </ActionButton>
      </PageHeader>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {filters.role && (
            <Chip
              label={`Role: ${roleOptions.find((r) => r.value === filters.role)?.label || filters.role}`}
              onDelete={() => handleFilterChange('role', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.status && (
            <Chip
              label={`Status: ${filters.status}`}
              onDelete={() => handleFilterChange('status', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.sport_id && (
            <Chip
              label={`Sport: ${sportsList.find((s) => s.id.toString() === filters.sport_id)?.name || filters.sport_id}`}
              onDelete={() => handleFilterChange('sport_id', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.location && (
            <Chip
              label={`Location: ${filters.location}`}
              onDelete={() => {
                handleFilterChange('location', '');
                handleFilterChange('user_latitude', '');
                handleFilterChange('user_longitude', '');
              }}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? "#ffffff" : "#1a202c", pb: 2 }}>
          Filter Users
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(userType === "admin" || userType === "master_org") && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sport</InputLabel>
                  <Select
                    value={filters.sport_id}
                    label="Sport"
                    onChange={(e) => handleFilterChange('sport_id', e.target.value)}
                    sx={{
                      color: isDark ? "#ffffff" : "#1a202c",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                      },
                    }}
                  >
                    <MenuItem value="">All Sports</MenuItem>
                    {sportsList.map((sport) => (
                      <MenuItem key={sport.id} value={sport.id.toString()}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, color: isDark ? "#cbd5e1" : "#718096" }}>
                Location (Optional - enter location and radius to filter by distance)
              </Typography>
              <LocationSearch
                value={filters.location}
                onChange={handleLocationSelect}
                placeholder="Enter location..."
                name="location"
                style={{
                  backgroundColor: isDark ? "#242424" : "#f7fafc",
                  color: isDark ? "#ffffff" : "#1a202c",
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            Clear All
          </Button>
          <Button
            onClick={() => setFilterDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: isDark ? "#6366f1" : "#1976d2",
              "&:hover": { backgroundColor: isDark ? "#818cf8" : "#1565c0" },
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Card noPadding>
        <DataTable
          columns={columns}
          data={datalist}
          loading={loading}
          actions={renderActions}
          pagination
          page={page}
          rowsPerPage={PAGE_LIMIT}
          totalCount={totalUsers}
          onPageChange={(e, newPage) => {
            setPage(newPage);
          }}
          onRowClick={(row) => {
            const role = getRoleForApi(row.role);
            setUserDetailsModal({ open: true, userId: row.id, role });
          }}
          emptyMessage="No users found"
          mobileTitleColumn="name"
          mobileHiddenColumns={["id"]}
        />
      </Card>

      {/* User Details Modal */}
      <UserDetailsModal
        open={userDetailsModal.open}
        onClose={() =>
          setUserDetailsModal({ open: false, userId: null, role: null, locationLabel: "" })
        }
        userId={userDetailsModal.userId}
        role={userDetailsModal.role}
        locationLabel={userDetailsModal.locationLabel}
        token={token}
        setIsLoaded={props.setIsLoaded}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.type === 'error' ? 'Delete' : 'Yes, change it'}
        cancelText="Cancel"
        onConfirm={async () => {
          if (confirmDialog.action) {
            await confirmDialog.action();
          }
          setConfirmDialog({ open: false, type: 'warning', title: '', message: '', action: null });
        }}
        onCancel={() => setConfirmDialog({ open: false, type: 'warning', title: '', message: '', action: null })}
      />

      {/* Organizations Dialog - only for admin */}
      {userType === "admin" && (
        <Dialog
          open={orgsDialog.open}
          onClose={() => setOrgsDialog({ open: false, user: null, orgs: [] })}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 2,
              borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
            }}
          >
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
              Clubs - {orgsDialog.user ? `${orgsDialog.user.first_name || ""} ${orgsDialog.user.last_name || ""}` : ""}
            </Typography>
            <IconButton
              onClick={() => setOrgsDialog({ open: false, user: null, orgs: [] })}
              sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, px: 3, maxHeight: "70vh", overflowY: "auto" }}>
            {orgsDialog.orgs.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No clubs found</Typography>
              </Box>
            ) : (
              <Box sx={{ pt: 1 }}>
                {orgsDialog.orgs.map((org, index) => {
                  const logoUrl = org.logo_url || org.logo || "";
                  const orgName = org.name || 
                                 org.organisation_name || 
                                 org.organization_name ||
                                 org.org_name ||
                                 "";
                  const orgEmail = org.user?.email || 
                                  org.email || 
                                  org.contact_email ||
                                  "";
                  const orgType = org.school_type || org.type || "";
                  // Check if it's a sub-org or paid org - backend provides is_sub_org and is_paid_org flags
                  const isSubOrg = org.is_sub_org === true || (org.master_org_id && org.is_sub_org !== false);
                  const isPaidOrg = org.is_paid_org === true || (!isSubOrg && !org.master_org_id && org.is_paid_org !== false);
                  const connectionStatus = org.connection_status || org.status || "";
                  
                  if (!orgName) return null;
                  
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        mb: 1,
                        borderRadius: "8px",
                        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                        border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                      }}
                    >
                      <Avatar
                        src={logoUrl}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                          color: isDark ? "#818cf8" : "#1976d2",
                          fontSize: "1.25rem",
                          fontWeight: 600,
                        }}
                      >
                        {orgName[0]?.toUpperCase() || "O"}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                            {orgName}
                          </Typography>
                          {isSubOrg && (
                            <Chip
                              label="Club"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                                color: isDark ? "#818cf8" : "#1976d2",
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {isPaidOrg && (
                            <Chip
                              label="Paid Org"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                backgroundColor: isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)",
                                color: isDark ? "#4ade80" : "#22c55e",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                        {orgEmail && (
                          <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                            {orgEmail}
                          </Typography>
                        )}
                        {orgType && (
                          <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                            Type: {orgType}
                          </Typography>
                        )}
                      </Box>
                      {connectionStatus && (
                        <StatusBadge status={connectionStatus} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
            <Button
              onClick={() => setOrgsDialog({ open: false, user: null, orgs: [] })}
              sx={{ textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default User;
